import moment from 'moment'
import axios from 'axios'
import baseconfig from '../../../../variables/config'

export const formatPence = pence =>
  '£' + (Number(pence || 0) / 100).toFixed(2)

export const mergeById = (a = [], b = [], idKey = 'id') => {
  const seen = new Set()
  const out = []
  for (const list of [a, b]) {
    if (!Array.isArray(list)) continue
    for (const item of list) {
      if (!item || item[idKey] == null) continue
      const k = String(item[idKey])
      if (seen.has(k)) continue
      seen.add(k)
      out.push(item)
    }
  }
  return out
}

export const sumVisitorAgeBuckets = booking => {
  const vd = booking.visitorDetails
  if (!vd) {
    return {
      under12months: 0,
      oneYearOlds: 0,
      twoToThree: 0,
      fourAndOver: 0,
      adults: 0
    }
  }
  return {
    under12months: (vd.under12mnths && vd.under12mnths.length) || 0,
    oneYearOlds: (vd.oneYearOlds && vd.oneYearOlds.length) || 0,
    twoToThree: (vd.twoToThree && vd.twoToThree.length) || 0,
    fourAndOver: (vd.fourAndOver && vd.fourAndOver.length) || 0,
    adults: (vd.adults && vd.adults.length) || 0
  }
}

const countBy = (bookings, getKey) => {
  const counts = {}
  bookings.forEach(booking => {
    const key = getKey(booking)
    if (key == null || key === '') return
    counts[key] = (counts[key] || 0) + 1
  })
  return Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
}

export const emptyPartyStats = () => ({
  totalParties: 0,
  totalChildren: 0,
  totalExtraGuests: 0,
  totalYoungSiblings: 0,
  avgChildrenPerParty: 0,
  totalGuests: 0,
  totalRevenuePence: 0,
  totalDepositsPence: 0,
  totalDiscountedPence: 0,
  wheelchairAccess: 0,
  partyTypes: [],
  childAges: [],
  partyReasons: [],
  menuOptions: [],
  timeSlots: [],
  popularAddOns: []
})

export const computePartyInsights = partyBookings => {
  if (!partyBookings.length) return emptyPartyStats()

  let totalChildren = 0
  let totalExtraGuests = 0
  let totalYoungSiblings = 0
  let totalRevenuePence = 0
  let totalDepositsPence = 0
  let totalDiscountedPence = 0
  let wheelchairAccess = 0
  const addOnCounts = {}

  const addItemCounts = items => {
    if (!Array.isArray(items)) return
    items.forEach(item => {
      if ((item.quantity || 0) > 0) {
        addOnCounts[item.name] =
          (addOnCounts[item.name] || 0) + item.quantity
      }
    })
  }

  partyBookings.forEach(booking => {
    const ad = booking.attendeeDetails || {}
    const dd = booking.discountDetails || {}
    const extra = booking.additionalDetails || {}

    totalChildren += ad.quantity || 0
    totalExtraGuests += ad.extraGuests || 0
    totalYoungSiblings += ad.youngSiblings || 0
    totalRevenuePence += ad.totalPrice || 0
    totalDepositsPence += ad.totalDeposit || 0
    if (dd.discountedTotal) totalDiscountedPence += dd.discountedTotal
    if (extra.requireWheelchairAccess) wheelchairAccess += 1

    addItemCounts(booking.extraItems)
    addItemCounts(booking.themeItems)
    addItemCounts(booking.mascotItems)
  })

  const n = partyBookings.length
  const popularAddOns = Object.entries(addOnCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)

  return {
    totalParties: n,
    totalChildren,
    totalExtraGuests,
    totalYoungSiblings,
    totalGuests: totalChildren + totalExtraGuests + totalYoungSiblings,
    avgChildrenPerParty: Math.round((totalChildren / n) * 10) / 10,
    totalRevenuePence,
    totalDepositsPence,
    totalDiscountedPence,
    wheelchairAccess,
    partyTypes: countBy(partyBookings, b => b.partyType || 'Unknown'),
    childAges: countBy(partyBookings, b => {
      const age =
        b.additionalDetails && b.additionalDetails.howOldWillTheyBe
      return age != null ? `Age ${age}` : null
    }),
    partyReasons: countBy(
      partyBookings,
      b =>
        b.additionalDetails && b.additionalDetails.partyReason
    ),
    menuOptions: countBy(
      partyBookings,
      b =>
        b.additionalDetails && b.additionalDetails.menuOptions
    ),
    timeSlots: countBy(partyBookings, b => b.partyTime),
    popularAddOns
  }
}

export const computePlayAgeStats = bookings => {
  let under12months = 0
  let oneYearOlds = 0
  let twoToThree = 0
  let fourAndOver = 0
  let adults = 0

  bookings.forEach(booking => {
    const b = sumVisitorAgeBuckets(booking)
    under12months += b.under12months
    oneYearOlds += b.oneYearOlds
    twoToThree += b.twoToThree
    fourAndOver += b.fourAndOver
    adults += b.adults
  })

  const ageStats = {
    under12months,
    oneYearOlds,
    twoToThree,
    fourAndOver,
    adults
  }

  return {
    ageStats,
    pieData: [
      { name: 'Adults', value: adults },
      { name: 'Under 1', value: under12months },
      { name: '1-2 YO', value: oneYearOlds },
      { name: '2-3 YO', value: twoToThree },
      { name: '4+ YO', value: fourAndOver }
    ],
    visitorCount:
      under12months + oneYearOlds + twoToThree + fourAndOver + adults
  }
}

const rangeFilter = (startDate, endDate, dateProp) => {
  const start = moment(startDate)
  start.set({ hour: 0, minute: 0 })
  const end = moment(endDate)
  end.set({ hour: 24, minute: 0 })
  return {
    params: {
      filter: {
        where: {
          and: [
            { [dateProp]: { gt: new Date(start.format()) } },
            { [dateProp]: { lt: new Date(end.format()) } }
          ]
        },
        order: dateProp + ' ASC'
      }
    }
  }
}

export const fetchPlayBookingsInRange = async (startDate, endDate, mode) => {
  const dateProp = mode === 'Bookings' ? 'date' : 'orderDate'
  const playPath =
    baseconfig.serverURL + '/PlayOnlyBooking' + baseconfig.endpoint
  const ops = rangeFilter(startDate, endDate, dateProp)
  const res = await axios.get(playPath, ops)
  return Array.isArray(res.data) ? res.data : []
}

export const fetchPartyBookingsInRange = async (startDate, endDate, mode) => {
  const dateProp = mode === 'Bookings' ? 'partyDate' : 'orderDate'
  const partyPath =
    baseconfig.serverURL + '/PartyBooking' + baseconfig.endpoint
  const ops = rangeFilter(startDate, endDate, dateProp)
  let live = []
  // Temporary: /archive scans the whole historical collection and can pin the
  // API disk. Restore this once the date indexes exist.
  let archived = []
  try {
    const liveRes = await axios.get(partyPath, ops)
    live = Array.isArray(liveRes.data) ? liveRes.data : []
  } catch (e) {
    console.log('fetchPartyBookingsInRange:', e.message)
  }
  // try {
  //   const archRes = await axios.get(partyPath + '/archive', ops)
  //   archived = Array.isArray(archRes.data) ? archRes.data : []
  // } catch (e) {
  //   console.log('fetchPartyBookingsInRange archive:', e.message)
  // }
  return mergeById(live, archived).filter(
    party => party.reference !== 'WKPBReservation'
  )
}
